<template>
    <div
        class="shrink-0 relative z-[1001] lg:!z-[1000]"
        :class="[isHeaderIniting && 'header-initing']"
    >
        <PtAIHistoryNav
            v-model="opened"
            :menus="navMenuList"
            :historyMenu="navHistory"
            :messages="historyPreTitleMessages"
            responsive
            @clickHistory="viewResult"
            @removeHistory="onRemoveHistory"
        >
            <!-- pc端logo -->
            <template #productLogo="{ opened }">
                <div v-if="isHeaderIniting" class="common-nav-wide-logo w-full py-5 relative min-h-[72px]"></div>
                <a
                    v-else-if="headerService.whiteLabel && headerService.customLogoVisible"
                    class="common-nav-wide-logo w-full py-5 relative custom-logo min-h-[72px]"
                    :href="headerService.logoHref"
                >
                    <span class="px-3 flex justify-center">
                        <img
                            v-if="opened"
                            :src="isDark ? headerService.customWhiteLogoUrl : headerService.customLogoUrl"
                            class="custom-logo-img"
                        />
                        <img
                            v-else
                            :src="headerService.customLogoUrl"
                            class="w-6 h-6 object-contain"
                        />
                    </span>
                </a>
                <a
                    v-else
                    href="/home"
                    class="common-nav-wide-logo w-full py-5 relative"
                >
                    <span class="px-3 flex justify-center">
                        <PtIcon v-if="opened" :icon="Logo" class="!w-[196px] !h-[32px]"/>
                        <PtIcon v-else :icon="MiniLogo" class="!w-6 !h-6"/>
                    </span>
                </a>
            </template>

            <!-- 移动端logo -->
            <template #logo>
                <a
                    v-if="headerService.whiteLabel && headerService.customLogoVisible"
                    :href="headerService.logoHref"
                >
                    <img
                        :src="headerService.customLogoUrl"
                        alt="custom-logo"
                        class="custom-logo-img mobile"
                    >
                </a>
                <a v-else href="/home">
                    <PtIcon :icon="Logo" class="!w-[160px] !h-6"/>
                </a>
            </template>
            <template #avatar="{ opened }">
                <div class="eureka-nav-bar-avatar mt-auto border-solid border-white/20 mx-3 flex flex-row items-center gap-4 lg:border-t lg:flex-col lg:py-4 relative">
                    <Incognito v-if="incognitoVisible" class="w-full" :opened="opened" :menu="incognitoItem" @handleClick="removeAllHistory"/>
                    <div v-if="!isLogin && opened && showLoginTip" class="hidden absolute bottom-[80px] left-0 lg:block w-[196px] p-3 bg-white text-grey-450 rounded-lg shadow-lg">
                        <img :src="signUpBg" class="absolute z-1 top-0 left-0 w-full rounded-lg" />
                        <div class="mb-3 relative z-10 flex justify-between items-center">
                            <div class="font-semibold text-base">{{ t('GuestToMaster.subject') }}</div>
                            <PtIcon :icon="iconClose" @click="showLoginTip = false" class="!w-6 !h-6 fill-grey-450 cursor-pointer"/>
                        </div>
                        {{ t('GuestToMaster.rights') }}
                        <div class="absolute -bottom-[9px] left-[15px]}">
                            <ArrowDown />
                        </div>
                    </div>
                    <EditionTag :isLogin="isLogin" :open="opened" />
                    <PtNavUserAvatar :open="opened" :service="headerService" @clickLogin="handleLogin">
                        <template #userPackageType>
                            <UserPackageType />
                        </template>
                    </PtNavUserAvatar>
                </div>
            </template>
        </PtAIHistoryNav>
        <MobileTag :isLogin="isLogin" />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { PtAIHistoryNav, PtNavUserAvatar, PtIcon } from 'patsnap-biz';
import { type HeaderService } from '@patsnap-utils/biz';
import ArrowDown from './components/icons/ArrowDown.vue';
import Incognito from './components/Incognito.vue';
import EditionTag from '@eureka/edition-upgrade/src/components/edition/EditionTag.vue';
import MobileTag from '@eureka/edition-upgrade/src/components/edition-tag/MobileTag.vue';
import { cn, tw, en, jp } from './infrastructure/locales';
import { INCOGNITO_MENU_ITEM, ROUTE_NAME_WHITELIST } from './constants';
import { useNavBarState } from './composables/use-navbar-store';
import type { NAV_MENU_TYPE } from './types';
import { useNavBar } from './composables/use-nav-bar';
import { UserPackageType } from '@eureka/header-menu-3';
import { loginSDK } from '@eureka/login-sdk';
import { useDark } from '@vueuse/core'
import _ from 'lodash';

import signUpBg from './assets/sign-up-bg.png';
import iconClose from '@patsnap-ui/icon/assets/solid/CloseMedium.svg'
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import '@patsnap-ui/icon/assets/solid/AddLarge.svg';
import '@patsnap-ui/icon/assets/userpackage.svg';
import '@patsnap-ui/icon/assets/solid/rocket.svg';
import '@patsnap-ui/icon/assets/solid/NavWorkspace.svg'
import '@patsnap-ui/icon/assets/solid/ChartTopipc.svg'
import '@patsnap-ui/icon/assets/solid/ChartRing.svg'
import '@patsnap-ui/icon/assets/solid/FolderMany.svg';
import Logo from './assets/logo.svg';
import MiniLogo from './assets/mini-logo.svg';

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
});

const props = defineProps<{
    headerService: HeaderService,
    login?: () => void,
    navModuleType: NAV_MENU_TYPE
}>();

const isDark = useDark()

const { opened, currentDetailId, routeName, shouldSave, currentRouteFlag, updateNavStatus, save } = useNavBarState();

const { historyPreTitleMessages, viewResult, onRemoveHistory, removeAllHistory, navMenuList, navHistory } = useNavBar(props);

const isLogin = computed(() => {
    return props.headerService.userCenterService.logged;
});

const showLoginTip = ref(true);

const isHeaderIniting = ref(!!props.headerService.deferred.promise);

props.headerService.deferred.promise.then(() => {
    isHeaderIniting.value = false;
});

const incognitoItem = computed(() => {
    return {
        ...INCOGNITO_MENU_ITEM,
        title: t(`NavBar.${INCOGNITO_MENU_ITEM.type}`),
        tip: t(`NavBar.${INCOGNITO_MENU_ITEM.type}_TIP`),
    };
});

const incognitoVisible = computed(() => {
    return _.get(props.headerService.account, '_info.incognito_flag');
});

watch(() => currentRouteFlag.value, () => {
    if (_.includes(ROUTE_NAME_WHITELIST, routeName.value) || !currentDetailId.value) return;
    updateNavStatus(false, false);
}, { immediate: true });

watch(() => opened.value, (value: boolean) => {
    if (shouldSave.value) {
        save(value as boolean);
    }
})

function handleLogin() {
    if (props.login) {
        props.login();
        return
    }
    loginSDK.open().then(() => {
        window.location.reload()
    });
}

</script>

<style lang="scss">
.common-nav-wide-logo {
    &::after {
        content: '';
        @apply absolute left-3 right-3 bottom-0 h-px bg-white/20;
    }

    &.custom-logo {
        .custom-logo-img {
            max-height: 48px;
            max-width: 100%;
            height: auto;
            width: auto;
            @apply transition-all duration-200;

            &.mobile {
                max-height: 24px !important;
                width: auto;
                height: 24px !important;
                object-fit: contain;
                @apply transition-all duration-200;
            }
        }
    }
}
.nav-history-sub-item {
    &.active-menu-item, &.tippy-content-active {
        span:last-child {
           display: none !important;
        }
    }
}

// 添加移动端特定样式
.patsnap-biz-mobile-nav-bar {
    .custom-logo-img.mobile {
        max-height: 24px !important;
        height: 24px !important;
        width: auto;
        object-fit: contain;
    }
}

.header-initing {
    .patsnap-biz-nav-header, .eureka-nav-bar-avatar {
        display: none;
    }
}
</style>
